import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";

const navLinks = [
  { href: "/about", label: "About" },
  { href: "/hair", label: "Hair & Massage" },
  { href: "/products", label: "Products" },
  { href: "/clothing", label: "Clothing" },
  { href: "/community", label: "Community & Events" },
  { href: "/high-on-life", label: "High on Life" },
  { href: "/surfers-against-sewage", label: "Surfers Against Sewage" },
  { href: "/work-with-me", label: "Work With Me" },
  { href: "/surfers-against-sewage", label: "Donate" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const profileRef = useRef(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-sand transition-shadow duration-300 ${
        scrolled ? "shadow-sm" : ""
      }`}
      data-testid="navbar"
    >
      {/* Top row: Large centered logo + Sign In */}
      <div className="border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-center relative py-4 md:py-5">
          {/* Centered logo — large */}
          <Link to="/" className="flex items-center justify-center" data-testid="nav-logo">
            <img
              src="/justjjax-logo-transparent.png"
              alt="justjjax"
              className="h-16 sm:h-20 md:h-24 lg:h-28 object-contain"
            />
          </Link>

          {/* Sign In / Profile — absolute right */}
          <div className="absolute right-6 md:right-12 hidden lg:flex items-center gap-2">
            {user ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium transition-colors text-charcoal hover:text-black"
                  data-testid="nav-profile-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-sage/20 flex items-center justify-center">
                      <User size={12} className="text-sage" />
                    </div>
                  )}
                  <span className="hidden xl:inline max-w-[60px] truncate">{user.name?.split(" ")[0]}</span>
                </button>
                <AnimatePresence>
                  {profileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-ocean/5 py-2 min-w-[180px]"
                    >
                      <div className="px-4 py-2 border-b border-ocean/5">
                        <p className="text-sm font-medium text-charcoal truncate">{user.name}</p>
                        <p className="text-xs text-charcoal/50 truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={() => { setProfileOpen(false); logout(); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-charcoal hover:bg-peachlight hover:text-sunset transition-colors"
                        data-testid="nav-logout-btn"
                      >
                        <LogOut size={14} />
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium border border-charcoal/20 text-charcoal hover:bg-white/30 transition-all duration-300 whitespace-nowrap"
                data-testid="nav-login-btn"
              >
                <User size={12} />
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile hamburger — absolute right */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden absolute right-6 p-2 text-charcoal"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Bottom row: Nav links + action buttons, all centered */}
      <div className="hidden lg:block border-b border-black/5">
        <nav className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-4 xl:gap-5 h-10">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`text-[11px] font-medium tracking-wider uppercase transition-colors hover:text-amber whitespace-nowrap ${
                  location.pathname === link.href ? "text-amber" : "text-charcoal/70"
                }`}
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}

            <span className="w-px h-4 bg-charcoal/15" />

            <Link
              to="/curlscan"
              className="bg-olive text-white hover:bg-olive/80 rounded-full px-4 py-1 text-xs font-medium tracking-wide transition-all duration-300 whitespace-nowrap"
              data-testid="nav-curlscan-btn"
            >
              The Curl Edit
            </Link>
            <a
              href="https://tinyurl.com/2nxodcgu"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-ocean text-white hover:bg-charcoal rounded-full px-4 py-1 text-xs font-medium tracking-wide transition-all duration-300 whitespace-nowrap"
              data-testid="nav-book-btn"
            >
              Book Now
            </a>
          </div>
        </nav>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden backdrop-blur-md bg-white/95 border-b border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-base font-medium transition-colors ${
                    location.pathname === link.href ? "text-amber" : "text-charcoal hover:text-sage"
                  }`}
                  data-testid={`nav-mobile-link-${link.href.slice(1)}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-black/5 pt-4 flex flex-col gap-3">
                <Link
                  to="/curlscan"
                  className="bg-olive text-white rounded-full px-6 py-3 text-sm font-medium text-center"
                  data-testid="nav-mobile-curlscan-btn"
                >
                  The Curl Edit
                </Link>
                <a
                  href="https://tinyurl.com/2nxodcgu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-ocean text-white rounded-full px-6 py-3 text-sm font-medium text-center"
                  data-testid="nav-mobile-book-btn"
                >
                  Book Now
                </a>
                {user ? (
                  <button
                    onClick={() => { setMobileOpen(false); logout(); }}
                    className="flex items-center justify-center gap-2 border border-ocean/10 rounded-full px-6 py-3 text-sm font-medium text-charcoal"
                    data-testid="nav-mobile-logout-btn"
                  >
                    <LogOut size={14} />
                    Sign Out ({user.name?.split(" ")[0]})
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-2 border border-ocean/10 rounded-full px-6 py-3 text-sm font-medium text-charcoal"
                    data-testid="nav-mobile-login-btn"
                  >
                    <User size={14} />
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
